import { get as idbGet, set as idbSet } from 'idb-keyval';
import { create } from 'zustand';
import { Episode, PodcastId } from '../models/podcasts';
import { fetchPodcastEpisodeDescriptions } from '../utils/api';
import { default as debugLog } from '../utils/debug';
import { reportSentryWarning } from '../utils/sentry';
import { objectKeys } from '../utils/ts-extras';

//
// Podcast descriptions are stored in IndexedDB. Since it can handle a few MB easily, we store them in
// a single IndexedDB key of the form `descriptions-{podcastId}
// Upon startup, we grab all description keys but don't load the descriptions themselves. This is done
// on demand.
//

type DescriptionsStore = {
  // podcastId -> guidHash -> description
  descriptions: Partial<Record<PodcastId, Record<string, string>>>;
};

type DescriptionsStoreActions = {
  // These methods don't actually expose the description, rather they just try to make it
  // available in the descriptions store so the UI can look it up w/ a selector. I could
  // expose them here but don't really have a need to as the UI doesn't easily consume Promises.
  fetchPodcastDescriptionsFromApi: (podcastId: PodcastId) => Promise<void>;
  fetchEpisodeDescription: (episode: Episode) => Promise<void>;
  addLatestDescriptions: (
    podcastId: PodcastId,
    descriptions: Record<string, string>
  ) => void;
};

const log = (...args: any[]) => {
  debugLog({ logName: 'descriptions', logColor: 'darkgreen' }, ...args);
};

const useDescriptionsStore = create<
  DescriptionsStore & { actions: DescriptionsStoreActions }
>()((set, get) => ({
  descriptions: {},
  actions: {
    fetchPodcastDescriptionsFromApi: async (podcastId: PodcastId) => {
      log('Fetching descriptions for', podcastId);
      const startTime = Date.now();
      const descriptionsFromApi = await fetchPodcastEpisodeDescriptions(podcastId);
      // Add to memory
      set((state) => ({
        descriptions: {
          ...state.descriptions,
          [podcastId]: descriptionsFromApi,
        },
      }));
      // Add to IndexedDB
      await idbSet(`descriptions-${podcastId}`, descriptionsFromApi);
      log(
        'Fetched & stored',
        objectKeys(descriptionsFromApi).length,
        'descriptions in',
        Date.now() - startTime,
        'ms'
      );
    },
    fetchEpisodeDescription: async (episode: Episode) => {
      const { podcastId, guidHash } = episode;

      // If it's already in memory, we don't need to do anything
      const descriptionsInMemory = get().descriptions[podcastId];
      if (descriptionsInMemory && descriptionsInMemory[guidHash]) {
        log('Found description in memory for episode:', episode.id);
        return;
      }

      // Attempt to grab it from IndexedDB and stick it in memory
      try {
        const startTime = Date.now();
        const descriptionsInIDB = await idbGet(`descriptions-${podcastId}`);
        log(
          'Found',
          descriptionsInIDB ? objectKeys(descriptionsInIDB).length : 'no',
          'descriptions from IDB in',
          Date.now() - startTime,
          'ms for episode:',
          episode.id
        );
        if (descriptionsInIDB && descriptionsInIDB[guidHash]) {
          // Add IDB descriptions to memory, but don't overwrite what's already there
          set((state) => ({
            descriptions: {
              ...state.descriptions,
              [podcastId]: {
                ...descriptionsInIDB,
                ...state.descriptions[podcastId],
              },
            },
          }));
          return;
        }
      } catch (err) {
        reportSentryWarning(err, {
          context: 'Failed to get descriptions from IndexedDB',
        });
      }

      // Fetch from API, storing it in memory & IndexedDB as well
      return get().actions.fetchPodcastDescriptionsFromApi(podcastId);
    },
    addLatestDescriptions: (podcastId, descriptionsFromApi) => {
      // Update latest episode descriptions in memory. Note that this may not be
      // the full set, but only the latest 20 episodes or so
      const descriptionsInMemory = get().descriptions[podcastId];
      set((state) => ({
        descriptions: {
          ...state.descriptions,
          [podcastId]: {
            ...descriptionsInMemory,
            ...descriptionsFromApi,
          },
        },
      }));
      // We could also update the descriptions in IndexedDB here, but that would
      // involve merging the values in memory w/ those in IndexedDB and it's simpler
      // to have a single update path fetchPodcastDescriptionsFromApi() w/ no race
      // conditions. This also makes it easy to call addLatestDescriptions() anytime
      // we fetch the latest episodes and not worry about performance since it doesn't
      // hit the disk. Of course it limits impact to only the most recent episodes,
      // but that's what users are mostly looking at anyway.
    },
  },
}));

//
// Expose actions
//

export const descriptionActions = useDescriptionsStore.getState().actions;
export const useEpisodeDescription = (episode: Episode | undefined) =>
  useDescriptionsStore((s) =>
    episode ? s.descriptions[episode.podcastId]?.[episode.guidHash] : undefined
  );
